import { NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { SuperLatexComponent, SuperLatexContent, SuperLatexEditorComponent } from '@examdojo/super-latex';
import { IonContent, IonModal } from '@ionic/angular/standalone';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { v4 as uuid } from 'uuid';
import { FormulaCalloutBlock } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { DEFAULT_FORMULA_CALLOUT_PROPS, FormulaBookletUrlService } from './formula-callout.model';

@Component({
  selector: 'dojo-formula-callout-node',
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    SuperLatexComponent,
    SuperLatexEditorComponent,
    IonModal,
    IonContent,
    TiptapNodeViewContentDirective,
    StopPropagationDirective,
    MarkdownViewerComponent,
    MatMenuModule,
    MatSlideToggle,
    MatTooltipModule,
    IconComponent,
  ],
  template: `
    <div
      class="formula-callout-wrapper formula-callout-container overflow-hidden"
      [class.rounded-2xl]="!borderless()"
      [class.border]="!borderless()"
      [class.border-neutral-200]="!borderless()"
      [ngClass]="customCss()"
      [ngStyle]="customStyle() ?? {}"
    >
      <!-- Settings button (top-right, edit mode only) -->
      @if (isEditable()) {
        <button
          class="settings-btn"
          [matMenuTriggerFor]="settingsMenu"
          (click)="$event.stopPropagation()"
          dojoStopPropagation
          title="Settings"
          contenteditable="false"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </svg>
        </button>

        <mat-menu #settingsMenu="matMenu" class="formula-callout-settings-menu">
          <div class="settings-menu-content" (click)="$event.stopPropagation()" dojoStopPropagation>
            <mat-slide-toggle
              color="primary"
              class="size-m full-row w-full"
              [checked]="centerFormula()"
              (change)="toggleCenterFormula()"
              labelPosition="before"
            >
              <span class="text-primary">Center formula</span>
            </mat-slide-toggle>
            <mat-slide-toggle
              color="primary"
              class="size-m full-row w-full"
              [checked]="showDerivation()"
              (change)="toggleShowDerivation()"
              labelPosition="before"
            >
              <span class="text-primary">Show derivation</span>
            </mat-slide-toggle>
            <mat-slide-toggle
              color="primary"
              class="size-m full-row w-full"
              [checked]="formulaBooklet()"
              (change)="toggleFormulaBooklet()"
              labelPosition="before"
            >
              <span class="text-primary">Formula booklet</span>
            </mat-slide-toggle>
            @if (formulaBooklet()) {
              <div class="formula-booklet-input-row">
                <label class="text-primary">URL override</label>
                <input
                  type="url"
                  class="formula-booklet-url-input"
                  [value]="formulaBookletUrl()"
                  (input)="onFormulaBookletUrlInput($event)"
                  (blur)="onFormulaBookletUrlInput($event)"
                  placeholder="https://..."
                  dojoStopPropagation
                />
              </div>
            }
            <mat-slide-toggle
              color="primary"
              class="size-m full-row w-full"
              [checked]="borderless()"
              (change)="toggleBorderless()"
              labelPosition="before"
            >
              <span class="text-primary">Borderless</span>
            </mat-slide-toggle>
          </div>
        </mat-menu>
      }

      <!-- Formula + Explanation row -->
      <div
        class="formula-row p-4"
        [class.bg-neutral-50]="!borderless()"
        [class.formula-row-no-center]="!centerFormula()"
      >
        @if (resolvedFormulaBookletUrl()) {
          <a
            class="formula-booklet-btn"
            [href]="resolvedFormulaBookletUrl()"
            target="_blank"
            rel="noopener noreferrer"
            matTooltip="This is given in the formula booklet"
            contenteditable="false"
            (click)="$event.stopPropagation()"
          >
            <dojo-icon icon="paperclip" faPrefix="fas" [size]="16" />
          </a>
        }
        @if (centerFormula()) {
          <div class="formula-spacer"></div>
        }
        <!-- Formula (SuperLatex), centered -->
        <div class="formula-area relative" contenteditable="false" (click)="openFormulaEditor()">
          @if (hasFormula()) {
            <dojo-super-latex [content]="formulaContent()" />
          } @else if (isEditable()) {
            <div class="formula-placeholder">Use AI to generate formula</div>
          }
        </div>

        <!-- Explanation: rendered markdown with click-to-edit -->
        @if (explanation() || isEditable()) {
          <div class="explanation-area text-neutral-600" contenteditable="false">
            @if (editingExplanation()) {
              <textarea
                #explanationTextarea
                class="explanation-textarea"
                [value]="explanation()"
                (input)="onExplanationInput($event)"
                (blur)="finishEditingExplanation($event)"
                (keydown.escape)="finishEditingExplanation($event)"
                placeholder="e.g. $x$ = value&#10;$P(x)$ = probability"
                rows="4"
                dojoStopPropagation
              ></textarea>
            } @else {
              <div
                class="explanation-preview"
                [class.explanation-preview-editable]="isEditable()"
                (click)="startEditingExplanation()"
              >
                @if (explanation()) {
                  <dojo-markdown-viewer [content]="explanation()" />
                } @else {
                  <span class="italic text-neutral-400">Add label...</span>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Derivation header: only shown when derivation is enabled -->
      @if (showDerivation()) {
        <div class="derivation-header" contenteditable="false">
          <button
            type="button"
            class="derivation-toggle"
            (click)="toggleDerivationExpanded()"
            [attr.aria-expanded]="derivationExpanded()"
            dojoStopPropagation
          >
            <span class="toggle-arrow" [class.expanded]="derivationExpanded()">&#9654;</span>
            Show derivation
          </button>
        </div>
      }

      <!-- Derivation content: ALWAYS in DOM, never destroyed -->
      <div
        class="derivation-content-wrapper"
        [class.derivation-visible]="showDerivation() && derivationExpanded()"
        [class.derivation-offscreen]="!showDerivation() || !derivationExpanded()"
      >
        <div class="formula-callout-derivation" tiptapNodeViewContent></div>
      </div>
    </div>

    <!-- SuperLatex Editor Modal -->
    <ion-modal
      cssClass="super-latex-editor-modal"
      [isOpen]="showFormulaEditor()"
      [backdropDismiss]="true"
      (didDismiss)="closeFormulaEditor()"
    >
      <ng-template>
        <ion-content [scrollY]="false">
          <dojo-super-latex-editor
            [content]="formulaContent()"
            (contentChange)="onFormulaChange($event)"
            (editorClose)="closeFormulaEditor()"
          />
        </ion-content>
      </ng-template>
    </ion-modal>
  `,
  styles: `
    $breakpoint-stack: 500px;

    :host {
      @apply block my-6;

      &.ProseMirror-selectednode {
        background-color: transparent !important;
        outline: none !important;
      }
    }

    .formula-callout-container {
      container-type: inline-size;
      position: relative;
    }

    .formula-booklet-btn {
      position: absolute;
      top: 14px;
      left: 14px;
      color: var(--neutral-400) !important;
      z-index: 5;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      text-decoration: none;
      transition: color 0.15s;

      &:hover {
        color: var(--neutral-500) !important;
      }

      @container (max-width: #{$breakpoint-stack}) {
        position: relative;
        top: auto;
        left: auto;
        align-self: flex-start;
      }
    }

    .formula-row {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      column-gap: 1.5rem;
      align-items: center;

      @container (max-width: #{$breakpoint-stack}) {
        display: flex;
        flex-direction: column;
      }
    }

    .formula-row-no-center {
      grid-template-columns: minmax(33.33%, 50%) 1fr;

      .formula-spacer {
        display: none;
      }

      @container (max-width: #{$breakpoint-stack}) {
        grid-template-columns: 1fr;
      }
    }

    .formula-spacer {
      @container (max-width: #{$breakpoint-stack}) {
        display: none;
      }
    }

    /* Formula area: centered in the middle column */
    .formula-area {
      display: flex;
      justify-content: center;
      min-width: 0;
      overflow: hidden;

      @container (max-width: #{$breakpoint-stack}) {
        width: 100%;
      }
    }

    .formula-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 60px;
      color: #a3a3a3;
      font-size: 0.875rem;
    }

    .settings-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 5px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      cursor: pointer;
      opacity: 0;
      transition:
        opacity 0.15s,
        background-color 0.15s;
      color: #9ca3af;
      z-index: 10;

      &:hover {
        background: #f3f4f6;
        color: #6b7280;
      }
    }

    .formula-callout-wrapper:hover .settings-btn {
      opacity: 1;
    }

    .explanation-area {
      @container (min-width: calc(#{$breakpoint-stack} + 1px)) {
        border-left: 1px solid var(--neutral-200);
        padding-left: 1.5rem;

        ::ng-deep {
          p {
            font-size: 0.84rem !important;
            line-height: 1.4 !important;
            margin: 0;
          }
          .katex {
            font-size: 1em !important;
          }
        }
      }

      @container (max-width: #{$breakpoint-stack}) {
        width: 100%;
        border-top: 1px solid var(--neutral-200);
        margin-top: 1rem;
        padding: 0;
      }
    }

    /* Explanation: click-to-edit preview */
    .explanation-preview {
      padding: 0.25rem;
      border-radius: 6px;
      min-height: 2rem;

      @container (max-width: #{$breakpoint-stack}) {
        padding: 1rem 0;
      }

      @container (max-width: #{$breakpoint-stack}) {
        padding: 1rem 0.25rem 0 0.25rem;
      }
    }

    .explanation-preview-editable {
      cursor: pointer;
      transition: background-color 0.15s;

      &:hover {
        background: rgba(255, 255, 255, 0.6);
      }
    }

    .explanation-textarea {
      width: 100%;
      resize: none;
      border-radius: 6px;
      border: 1px solid #c7d2fe;
      background: white;
      padding: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.4;
      outline: none;
      font-family: inherit;

      &:focus {
        border-color: #818cf8;
        box-shadow: 0 0 0 2px rgba(129, 140, 248, 0.15);
      }
    }

    /* Derivation header: toggle + checkbox on same row */
    .derivation-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-top: 1px solid #e5e7eb;
      background: white;
    }

    .derivation-toggle {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--ion-color-medium, #9ca3af);
      cursor: pointer;
      background: transparent;
      border: none;
      padding: 0.25rem 0;

      &:hover {
        color: #6b7280;
      }
    }

    .toggle-arrow {
      font-size: 0.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s ease;

      &.expanded {
        transform: rotate(90deg);
      }
    }

    ::ng-deep .formula-callout-settings-menu {
      .mat-mdc-menu-content {
        padding: 0;
      }
    }

    .settings-menu-content {
      padding: 0.75rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      min-width: 200px;
    }

    .formula-booklet-input-row {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .formula-booklet-url-input {
      width: 100%;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 0.375rem 0.5rem;
      font-size: 0.8125rem;
      outline: none;
      font-family: inherit;

      &:focus {
        border-color: #818cf8;
        box-shadow: 0 0 0 2px rgba(129, 140, 248, 0.15);
      }

      &::placeholder {
        color: #c7d2fe;
      }
    }

    /* Derivation content: use offscreen positioning instead of height:0 */
    .derivation-visible {
      padding: 0 1rem 0.5rem;
      background: white;

      .formula-callout-derivation {
        min-height: 40px;
      }
    }

    .derivation-offscreen {
      position: absolute;
      left: -9999px;
      visibility: hidden;
      pointer-events: none;
    }
  `,
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => FormulaCalloutNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulaCalloutNodeComponent extends NodeViewComponent<FormulaCalloutBlock> {
  private readonly formulaBookletUrlService = inject(FormulaBookletUrlService, { optional: true });
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly superLatexChildren = viewChildren(SuperLatexComponent);

  readonly showFormulaEditor = signal(false);
  readonly editingExplanation = signal(false);
  readonly derivationExpanded = signal(false);

  readonly formula = this.getProp('formula');
  readonly explanation = this.getProp('explanation');
  readonly showDerivation = this.getProp('show_derivation');
  readonly centerFormula = this.getProp('center_formula');
  readonly formulaBooklet = this.getProp('formula_booklet');
  readonly formulaBookletUrl = this.getProp('formula_booklet_url');
  readonly borderless = this.getProp('borderless');
  readonly customCss = this.getProp('custom_css');
  readonly customStyle = this.getProp('custom_style');

  /** Per-block URL override wins; otherwise fall back to the default URL when formula_booklet is on. */
  readonly resolvedFormulaBookletUrl = computed(() => {
    const override = this.formulaBookletUrl();
    if (override) {
      return override;
    }
    if (this.formulaBooklet()) {
      return this.formulaBookletUrlService?.url() ?? '';
    }
    return '';
  });

  readonly isEditable = computed(() => this.editor().isEditable);

  readonly hasFormula = computed(() => {
    const f = this.formula();
    return f?.latex_nodes?.length > 0;
  });

  readonly formulaContent = computed<SuperLatexContent>(() => {
    const f = this.formula();
    return {
      latex_nodes: f?.latex_nodes ?? [],
      annotations: f?.annotations ?? undefined,
      connections: f?.connections ?? undefined,
      layout: f?.layout ?? undefined,
      highlighted_anchors: f?.highlighted_anchors ?? undefined,
    };
  });

  override getDefaults = (): FormulaCalloutBlock => ({
    ...DEFAULT_FORMULA_CALLOUT_PROPS,
    id: uuid(),
  });

  openFormulaEditor(): void {
    if (this.isEditable()) {
      this.showFormulaEditor.set(true);
    }
  }

  closeFormulaEditor(): void {
    this.showFormulaEditor.set(false);
  }

  onFormulaChange(updatedContent: SuperLatexContent): void {
    this.updateProp('formula', {
      latex_nodes: updatedContent.latex_nodes,
      annotations: updatedContent.annotations,
      connections: updatedContent.connections,
      layout: updatedContent.layout,
      highlighted_anchors: updatedContent.highlighted_anchors,
    });
  }

  startEditingExplanation(): void {
    if (this.isEditable()) {
      this.editingExplanation.set(true);
      setTimeout(() => {
        const textarea = this.elementRef.nativeElement.querySelector('.explanation-textarea') as HTMLTextAreaElement;
        textarea?.focus();
      });
    }
  }

  onExplanationInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.updateProp('explanation', target.value);
  }

  finishEditingExplanation(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.updateProp('explanation', target.value);
    this.editingExplanation.set(false);
  }

  toggleDerivationExpanded(): void {
    this.derivationExpanded.update((v) => !v);
  }

  toggleShowDerivation(): void {
    this.updateProp('show_derivation', !this.showDerivation());
  }

  toggleCenterFormula(): void {
    this.updateProp('center_formula', !this.centerFormula());
    setTimeout(() => this.superLatexChildren().forEach((child) => child.repaint()), 50);
  }

  toggleFormulaBooklet(): void {
    this.updateProp('formula_booklet', !this.formulaBooklet());
  }

  toggleBorderless(): void {
    this.updateProp('borderless', !this.borderless());
  }

  onFormulaBookletUrlInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.updateProp('formula_booklet_url', target.value);
  }
}
